import type { EventProvider, MessageProvider } from '@prisma/client';
import { z } from 'zod';
import type { ContentActionResult } from './content-catalog';

const gmailDraftEmailSchema = z.string().trim().email().max(320);
const gmailDraftEmailListSchema = z.array(gmailDraftEmailSchema).max(100);

export const messageDraftModeSchema = z.enum([
  'reply',
  'replyAll',
  'forward',
  'newFromContext',
]);
export type MessageDraftMode = z.infer<typeof messageDraftModeSchema>;

export interface MessageDraftResolved {
  to: string[];
  cc: string[];
  bcc: string[];
  replyTo: string[];
  subject: string;
  body: string;
}

export interface MessageDraftDefaults {
  byMode: Record<MessageDraftMode, MessageDraftResolved>;
  threadContext: {
    sourceMessageId: number;
    rootCode: string | null;
    providerThreadId: string | null;
    messageIds: number[];
    boundedCount: number;
  };
  replyCapable: boolean;
  provider: MessageProvider;
  actingAccountId: number | null;
  actingEmail: string | null;
}

export const createMessageReplyDraftSchema = z.object({
  memberExternalAccountId: z.number().int().positive().optional(),
  mode: messageDraftModeSchema,
  to: gmailDraftEmailListSchema.optional(),
  cc: gmailDraftEmailListSchema.optional(),
  bcc: gmailDraftEmailListSchema.optional(),
  replyTo: gmailDraftEmailListSchema.optional(),
  subject: z.string().trim().max(998).optional(),
  body: z.string().max(100_000).optional(),
  htmlBody: z.string().max(1_000_000).optional(),
  attachmentContentIds: z.array(z.number().int().positive()).max(10).optional(),
});
export type CreateMessageReplyDraftInput = z.infer<
  typeof createMessageReplyDraftSchema
>;

export const createMessageDraftFromDocumentSchema = z.object({
  memberExternalAccountId: z.number().int().positive(),
  to: gmailDraftEmailListSchema.min(1),
  cc: gmailDraftEmailListSchema.optional(),
  bcc: gmailDraftEmailListSchema.optional(),
  replyTo: gmailDraftEmailListSchema.optional(),
  subject: z.string().trim().max(998).optional(),
  body: z.string().max(100_000).optional(),
  htmlBody: z.string().max(1_000_000).optional(),
});
export type CreateMessageDraftFromDocumentInput = z.infer<
  typeof createMessageDraftFromDocumentSchema
>;

export const createMessageComposeDraftSchema = z.object({
  memberExternalAccountId: z.number().int().positive().optional(),
  to: gmailDraftEmailListSchema.optional(),
  cc: gmailDraftEmailListSchema.optional(),
  bcc: gmailDraftEmailListSchema.optional(),
  replyTo: gmailDraftEmailListSchema.optional(),
  subject: z.string().trim().max(998).optional(),
  body: z.string().max(100_000).optional(),
  htmlBody: z.string().max(1_000_000).optional(),
  attachmentContentIds: z.array(z.number().int().positive()).max(10).optional(),
});
export type CreateMessageComposeDraftInput = z.infer<
  typeof createMessageComposeDraftSchema
>;

export interface CreateMessageDraftActionResult extends ContentActionResult {
  success: true;
  sourceContentId: number | null;
  draftContentId: number;
  draftCode: string;
  draftId: string | null;
  providerMessageId: string | null;
  providerThreadId: string | null;
  resolved: {
    to: string[];
    cc: string[];
    bcc: string[];
    subject: string;
    authoredBody: string;
  };
  reusedExistingDraft: boolean;
}

export const updateMessageDraftSchema = z
  .object({
    authoredBody: z.string().max(100_000).optional(),
    to: gmailDraftEmailListSchema.optional(),
    cc: gmailDraftEmailListSchema.optional(),
    bcc: gmailDraftEmailListSchema.optional(),
    subject: z.string().trim().max(998).optional(),
  })
  .strict()
  .refine(
    (value) =>
      value.authoredBody !== undefined ||
      value.to !== undefined ||
      value.cc !== undefined ||
      value.bcc !== undefined ||
      value.subject !== undefined,
    { message: 'At least one of authoredBody, to, cc, bcc, subject is required' }
  );
export type UpdateMessageDraftInput = z.infer<typeof updateMessageDraftSchema>;

export interface UpdateMessageDraftActionResult extends ContentActionResult {
  success: true;
  draftContentId: number;
  draftCode: string;
  sourceContentId: number | null;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  authoredBody: string;
  providerLinkage: {
    provider: MessageProvider;
    draftId: string | null;
    providerMessageId: string | null;
    providerThreadId: string | null;
  };
}

export const sendMessageDraftSchema = z.object({}).strict();
export type SendMessageDraftInput = z.infer<typeof sendMessageDraftSchema>;

export const discardMessageDraftSchema = z.object({}).strict();
export type DiscardMessageDraftInput = z.infer<typeof discardMessageDraftSchema>;

export interface DiscardMessageDraftActionResult extends ContentActionResult {
  success: true;
  discardedContentId: number;
  discardedCode: string;
}

export interface SendMessageDraftActionResult extends ContentActionResult {
  success: true;
  sourceContentId: number;
  sentAt: string;
  providerMessageId: string | null;
  providerThreadId: string | null;
}

export interface GovernedPendingApprovalResponse {
  status: 'PENDING_APPROVAL';
  toolCallId: string;
  approvalRequestId: string;
  message: string;
}

export interface AttachContentToDocumentNodeActionInput {
  sourceContentId: number;
  targetDocumentNodeId: number;
  confidenceSignal: {
    score: number;
    threshold: number;
    rationale: string[];
  };
}

export interface AttachContentToDocumentNodeActionResult extends ContentActionResult {
  success: true;
  sourceContentId: number;
  targetDocumentNodeId: number;
  rootCode: string;
}

export interface GmailImportDocumentOutcome {
  groupingsProposed: number;
  groupingsAccepted: number;
  groupingsDeclined: number;
  documentsCreated: number;
  lateAttachmentsConsidered: number;
  lateAttachmentsApplied: number;
  lateAttachmentsSkipped: number;
  createdDocumentIds: number[];
  declinedReasons: Array<{ groupingIndex: number | null; reason: string }>;
}

const calendarIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(512)
  .regex(/^\S+$/, 'providerCalendarId must not contain whitespace');

export const createCalendarEventFromEventContextSchema = z.object({
  memberExternalAccountId: z.number().int().positive(),
  providerCalendarId: calendarIdSchema.optional(),
});
export type CreateCalendarEventFromEventContextInput = z.infer<
  typeof createCalendarEventFromEventContextSchema
>;

export const updateCalendarEventFromEventContextSchema = z.object({
  memberExternalAccountId: z.number().int().positive(),
  providerCalendarId: calendarIdSchema.optional(),
});
export type UpdateCalendarEventFromEventContextInput = z.infer<
  typeof updateCalendarEventFromEventContextSchema
>;

export interface CalendarEventWriteBackActionResult extends ContentActionResult {
  success: true;
  sourceContentId: number;
  memberExternalAccountId: number;
  provider: 'GOOGLE_CALENDAR' | 'VIVPORT' | 'MICROSOFT_CALENDAR';
  providerCalendarId: string;
  providerEventId: string;
  iCalUid: string | null;
  synchronizedAt: string;
  created: boolean;
}

export const createCalendarEventFromScratchSchema = z.object({
  memberExternalAccountId: z.number().int().positive().optional(),
  providerCalendarId: z.string().trim().max(1024).optional(),
  title: z.string().trim().min(1).max(998),
  description: z.string().max(10_000).optional(),
  location: z.string().max(1024).optional(),
  startAt: z.string().datetime(),
  endAt: z.string().datetime(),
  isAllDay: z.boolean(),
  timezone: z.string().max(128).optional(),
  attendees: z
    .array(
      z.object({
        email: z.string().trim().email().max(320),
        displayName: z.string().max(256).optional(),
        optional: z.boolean().optional(),
      })
    )
    .max(100)
    .optional(),
  sendInvite: z.boolean().optional(),
});
export type CreateCalendarEventFromScratchInput = z.infer<typeof createCalendarEventFromScratchSchema>;

export interface CreateCalendarEventFromScratchActionResult extends ContentActionResult {
  success: true;
  contentId: number;
  code: string;
  provider: EventProvider;
  providerCalendarId: string;
  providerEventId: string;
  iCalUid: string | null;
  sendInvite: boolean;
  attendeesCount: number;
}
